import { useEffect, useState } from "react";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import { TrendingUp, Trophy, Award, Medal, FileDown, FileSpreadsheet, Filter, Loader2, XCircle, Building2 } from "lucide-react";

const AXIS_TICK = { fontSize: 12, fill: "#64748B" };
const AXIS_LINE = { stroke: "#E2E8F0" };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_RADIUS = [3, 3, 0, 0];

const Stat = ({ label, value, tone }) => (
  <Card className="p-5 border-slate-200">
    <p className="nx-label">{label}</p>
    <p className={`text-3xl font-bold font-heading mt-1 ${tone}`}>{value}</p>
  </Card>
);

const rankIcon = (i) => {
  if (i === 0) return <Trophy className="h-4 w-4 text-amber-500" />;
  if (i === 1) return <Award className="h-4 w-4 text-slate-400" />;
  if (i === 2) return <Medal className="h-4 w-4 text-amber-700" />;
  return <span className="text-xs font-bold text-slate-400 w-4 text-center">{i + 1}</span>;
};

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");
  const [exporting, setExporting] = useState("");

  const load = async () => {
    setData(null);
    try {
      const params = {};
      if (fromMonth && toMonth) { params.from_month = fromMonth; params.to_month = toMonth; }
      const { data } = await api.get("/stats", { params });
      setData(data);
    } catch {
      setData({ monthly: [], leaderboard: [], totals: {}, period_label: "" });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const applyFilter = () => {
    if (fromMonth && toMonth) load();
    else toast.error("Seleziona sia il mese di inizio che di fine");
  };

  const resetFilter = () => { setFromMonth(""); setToMonth(""); setTimeout(load, 0); };

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const params = { format };
      if (fromMonth && toMonth) { params.from_month = fromMonth; params.to_month = toMonth; }
      const res = await api.get("/stats/export", { params, responseType: "blob" });
      const type = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statistiche_Nexus.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Esportazione non riuscita");
    } finally { setExporting(""); }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1">Storico e Statistiche</h1>
            <p className="text-sm text-slate-500">Andamento mensile dei trasporti e classifica partner{data?.period_label ? ` · ${data.period_label}` : ""}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-pdf" variant="outline" className="gap-2" onClick={() => exportReport("pdf")} disabled={!!exporting}>
              {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
            </Button>
            <Button data-testid="export-xlsx" variant="outline" className="gap-2" onClick={() => exportReport("xlsx")} disabled={!!exporting}>
              {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
            </Button>
          </div>
        </div>

        {/* Period filter */}
        <Card className="p-4 border-slate-200 mb-6 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mr-2">
            <Filter className="h-4 w-4 text-primary" /> Periodo
          </div>
          <div>
            <Label className="nx-label">Da (mese)</Label>
            <Input data-testid="from-month" type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="mt-1.5 h-9 w-44" />
          </div>
          <div>
            <Label className="nx-label">A (mese)</Label>
            <Input data-testid="to-month" type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className="mt-1.5 h-9 w-44" />
          </div>
          <Button data-testid="apply-period" className="h-9" onClick={applyFilter}>Applica</Button>
          {(fromMonth || toMonth) && (
            <Button data-testid="reset-period" variant="ghost" className="h-9" onClick={resetFilter}>Ultimi 6 mesi</Button>
          )}
        </Card>

        {!data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-24" />)}</div>
            <Skeleton className="h-72" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Stat label="Totale Richieste" value={data.totals.total || 0} tone="text-slate-900" />
              <Stat label="Pubblicate" value={data.totals.pubblicata || 0} tone="text-[#A88E56]" />
              <Stat label="Assegnate" value={data.totals.assegnato || 0} tone="text-[#18314F]" />
              <Stat label="Completate" value={data.totals.completata || 0} tone="text-[#15803D]" />
              <Stat label="Rifiuti" value={data.totals.rifiuti || 0} tone="text-[#942F30]" />
            </div>

            <Card className="p-5 border-slate-200 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-slate-800 font-heading">Andamento Mensile</h3>
              </div>
              <div style={{ width: "100%", height: 300, minHeight: 300 }} data-testid="stats-chart">
                <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                  <BarChart data={data.monthly} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="label" tick={AXIS_TICK} axisLine={AXIS_LINE} />
                    <YAxis tick={AXIS_TICK} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                    <Bar dataKey="pubblicata" name="Pubblicate" fill="#C6AD76" radius={BAR_RADIUS} />
                    <Bar dataKey="assegnato" name="Assegnate" fill="#18314F" radius={BAR_RADIUS} />
                    <Bar dataKey="completata" name="Completate" fill="#4E5D70" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-slate-800 font-heading">Classifica Partner</h3>
              </div>
              {data.leaderboard.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">Nessun servizio assegnato nel periodo.</p>
              ) : (
                <div className="space-y-2">
                  {data.leaderboard.map((p, i) => (
                    <div key={`lb-${p.name}-${i}`} data-testid={`leaderboard-row-${i}`}
                      className="flex items-center gap-3 rounded-md border border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="w-6 flex justify-center">{rankIcon(i)}</div>
                      <p className="flex-1 text-sm font-medium text-slate-800">{p.name}</p>
                      <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-0.5">{p.assegnati} assegnati</span>
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-0.5">{p.completati} completati</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {data.by_richiedente && data.by_richiedente.length > 0 && (
              <Card className="p-5 border-slate-200 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold text-slate-800 font-heading">Richieste per Richiedente</h3>
                </div>
                <div className="space-y-2">
                  {data.by_richiedente.map((r, i) => (
                    <div key={`req-${r.name}-${i}`} data-testid={`richiedente-row-${i}`}
                      className="flex items-center gap-3 rounded-md border border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors">
                      <span className="text-xs font-bold text-slate-400 w-6 text-center">{i + 1}</span>
                      <p className="flex-1 text-sm font-medium text-slate-800">{r.name}</p>
                      <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-0.5">{r.count} richieste</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {data.rejections_leaderboard && data.rejections_leaderboard.length > 0 && (
              <Card className="p-5 border-slate-200 mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <h3 className="font-semibold text-slate-800 font-heading">Rifiuti per Partner</h3>
                </div>
                <div className="space-y-2">
                  {data.rejections_leaderboard.map((p, i) => (
                    <div key={`rj-${p.name}-${i}`} data-testid={`rejections-row-${i}`}
                      className="flex items-center gap-3 rounded-md border border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors">
                      <span className="text-xs font-bold text-slate-400 w-6 text-center">{i + 1}</span>
                      <p className="flex-1 text-sm font-medium text-slate-800">{p.name}</p>
                      <span className="text-xs font-semibold text-red-700 bg-red-50 rounded-full px-2.5 py-0.5">{p.rifiuti} rifiuti</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}